import React from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import AppSidebarV2 from "../components/layout/AppSidebarV2";
import MobileQuickNav from "../components/layout/MobileQuickNav";
import BreadcrumbsSchema from "../components/BreadcrumbsSchema";
import { literatureApi } from "../api/literatureApi";
import { useAuth } from "../auth/authStore";
import { useToast } from "../ui/toast/ToastProvider";
import { useNotifications } from "../notifications/notificationsStore";
import { getSiteOrigin, joinUrl } from "../utils/seo";
import {
  isExternalHttpUrl,
  resolvePsychologistProfileRoute,
} from "../utils/psychologistProfile";

const CATALOG_PAGE_SIZE = 20;
const MINI_BOOKS_LIMIT = 10;
const SAVED_LIMIT = 10;
const MY_BOOKS_LIMIT = 10;

function asObject(value) {
  return value && typeof value === "object" ? value : {};
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function unwrapItems(payload) {
  const raw = asObject(payload);
  if (raw.item && typeof raw.item === "object") return raw.item;
  if (Array.isArray(raw.items)) return raw.items;
  if (raw.items && typeof raw.items === "object") return raw.items;
  if (raw.item && typeof raw.item === "object" && Array.isArray(raw.item.items)) return raw.item.items;
  if (raw.item && typeof raw.item === "object" && raw.item.items && typeof raw.item.items === "object") {
    return raw.item.items;
  }
  return raw;
}

function extractCollection(value) {
  if (Array.isArray(value)) return value;
  const obj = asObject(value);
  if (Array.isArray(obj.content)) return obj.content;
  if (Array.isArray(obj.items)) return obj.items;
  if (obj.items && typeof obj.items === "object" && Array.isArray(obj.items.content)) return obj.items.content;
  if (Array.isArray(obj.item)) return obj.item;
  if (obj.item && typeof obj.item === "object" && Array.isArray(obj.item.content)) return obj.item.content;
  return [];
}

function unwrapPage(payload) {
  const candidate = unwrapItems(payload);
  if (candidate && typeof candidate === "object" && Array.isArray(candidate.content)) return candidate;
  if (Array.isArray(candidate)) {
    return {
      content: candidate,
      number: 0,
      size: candidate.length,
      totalElements: candidate.length,
      totalPages: candidate.length ? 1 : 0,
      first: true,
      last: true,
      empty: candidate.length === 0,
    };
  }
  return {
    content: [],
    number: 0,
    size: 0,
    totalElements: 0,
    totalPages: 0,
    first: true,
    last: true,
    empty: true,
  };
}

function normalizeCatalogErrorMessage(error, fallback = "Не удалось загрузить литературу") {
  const status = Number(error?.status || 0);
  const raw = String(error?.message || error?.error || error?.code || "").trim();
  const upper = raw.toUpperCase();

  if (upper.includes("LITERATURE_BOOK_NOT_FOUND")) return "Книга не найдена.";
  if (upper.includes("LITERATURE_TOPIC_NOT_FOUND")) return "Раздел литературы не найден.";
  if (upper.includes("LITERATURE_ACCESS_DENIED")) return "Нет доступа к разделу литературы.";

  if (status === 401 || status === 403) return "Нужно авторизоваться, чтобы просматривать литературу.";
  if (status === 404) return "Каталог литературы сейчас недоступен.";
  if (status === 429) return "Слишком много запросов. Попробуйте снова через минуту.";

  if (
    status >= 500 ||
    upper.includes("DATABASE ERROR") ||
    upper.includes("INTERNAL SERVER ERROR") ||
    upper.includes("SQL")
  ) {
    return "Каталог временно недоступен. Попробуйте обновить страницу чуть позже.";
  }

  if (/[А-Яа-яЁё]/.test(raw)) return raw;
  return fallback;
}

function safeNumber(value, fallback = 0) {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
}

function firstFiniteNumber(...values) {
  for (const value of values) {
    const num = Number(value);
    if (Number.isFinite(num)) return num;
  }
  return NaN;
}

function normalizeBook(raw = {}) {
  const item = asObject(raw);
  const authorInfo = asObject(item.authorProfile || item.authorDetails || item.author || item.psychologist || item.owner);
  const topicList = asArray(item.topics).map((v) => String(v || "").trim()).filter(Boolean);
  const tagList = asArray(item.tags).map((v) => String(v || "").trim()).filter(Boolean);
  const primaryTopic = String(item.primaryTopic || topicList[0] || "").trim();
  const topRecommender = asObject(item.topRecommender);
  const externalStore = asObject(item.externalStore);

  return {
    id: item.id ?? null,
    slug: String(item.slug || ""),
    title: String(item.title || "Без названия"),
    author: String(
      (typeof item.author === "string" ? item.author : authorInfo.name) || "Не указан"
    ),
    authorUserId: authorInfo.userId ?? authorInfo.id ?? item.authorUserId ?? item.psychologistUserId ?? null,
    authorProfileUrl: String(authorInfo.profileUrl || item.authorProfileUrl || ""),
    publishYear: item.publishYear ?? null,
    shortDescription: String(item.shortDescription || ""),
    coverUrl: String(item.coverUrl || ""),
    buyUrl: String(item.buyUrl || ""),
    difficultyLabel: String(item.difficultyLabel || item.difficultyLevel || ""),
    categoryLabel: String(item.categoryLabel || ""),
    primaryTopic,
    topics: topicList,
    tags: tagList,
    recommendedByCount: safeNumber(item.recommendedByPsychologistsCount, 0),
    topBadge: String(item.topBadge || ""),
    topRecommender: {
      userId: topRecommender.userId ?? null,
      name: String(topRecommender.name || ""),
      avatarUrl: String(topRecommender.avatarUrl || ""),
      headline: String(topRecommender.headline || ""),
    },
    saved: Boolean(item.saved),
    price: Number.isFinite(Number(item.price)) ? Number(item.price) : null,
    discountPercent: Math.max(0, Math.min(99, safeNumber(item.discountPercent ?? item.discount ?? item.salePercent, 0))),
    priceCurrency: String(item.priceCurrency || item.currency || "RUB").trim().toUpperCase() || "RUB",
    externalStore: {
      provider: String(externalStore.provider || ""),
      displayText: String(externalStore.displayText || ""),
      url: String(externalStore.url || ""),
    },
  };
}

function normalizeCatalog(payload = {}) {
  const raw = asObject(payload);
  const hero = asObject(raw.hero);
  const topicFilters = extractCollection(raw.topicFilters).map((v) => String(v || "").trim()).filter(Boolean);
  const featured = extractCollection(raw.featured).map(normalizeBook);
  const books = extractCollection(raw.books).map(normalizeBook);
  const latest = extractCollection(raw.latest).map((entry) => {
    const row = asObject(entry);
    return {
      book: normalizeBook(row.book || row),
      topRecommendation: asObject(row.topRecommendation),
    };
  });
  const sections = extractCollection(raw.sections).map((section) => {
    const sectionObj = asObject(section);
    return {
      topic: String(sectionObj.topic || "Без темы"),
      total: safeNumber(sectionObj.total, 0),
      items: extractCollection(sectionObj.items).map(normalizeBook),
    };
  });

  const booksPage = asObject(raw.books);
  const featuredPage = asObject(raw.featured);
  const total = Math.max(
    0,
    safeNumber(
      firstFiniteNumber(raw.total, raw.totalElements, raw.count, booksPage.totalElements, featuredPage.totalElements),
      0
    )
  );
  const booksCount = Math.max(
    0,
    safeNumber(
      firstFiniteNumber(
        hero.booksCount,
        hero.catalogBooksCount,
        raw.booksCount,
        raw.catalogBooksCount,
        total,
        books.length,
        featured.length
      ),
      0
    )
  );

  const expertNames = new Set(
    latest
      .map((row) =>
        String(
          row?.topRecommendation?.psychologistName ||
            row?.book?.topRecommender?.name ||
            ""
        )
          .trim()
          .toLowerCase()
      )
      .filter(Boolean)
  );
  const psychologistsCount = Math.max(
    0,
    safeNumber(
      firstFiniteNumber(
        hero.psychologistsCount,
        hero.psychologistsEditorsCount,
        hero.specialistsCount,
        raw.psychologistsCount,
        raw.psychologistsEditorsCount,
        raw.specialistsCount,
        expertNames.size
      ),
      0
    )
  );

  const topicsCount = Math.max(
    0,
    safeNumber(
      firstFiniteNumber(
        hero.topicsCount,
        hero.topicCount,
        raw.topicsCount,
        raw.topicCount,
        topicFilters.length,
        sections.length
      ),
      0
    )
  );

  return {
    hero: {
      booksCount,
      psychologistsCount,
      topicsCount,
    },
    topicFilters,
    featured,
    books,
    sections,
    latest,
    page: Math.max(0, safeNumber(firstFiniteNumber(raw.page, raw.number, booksPage.number, featuredPage.number), 0)),
    size: Math.max(
      1,
      safeNumber(firstFiniteNumber(raw.size, booksPage.size, featuredPage.size), CATALOG_PAGE_SIZE)
    ),
    total,
  };
}

function displayRole(role) {
  const value = String(role || "").toUpperCase();
  if (value === "ADMIN") return "Администратор";
  if (value === "PSYCHOLOGIST") return "Психолог";
  if (value === "MODERATOR") return "Модератор";
  if (value === "CLIENT") return "Клиент";
  return "Гость";
}

function formatNumber(value) {
  return new Intl.NumberFormat("ru-RU").format(Math.max(0, safeNumber(value, 0)));
}

function shortText(value, maxLength = 150) {
  const text = String(value || "").trim();
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1).trim()}…`;
}

function discountLabel(book) {
  const percent = Math.max(0, Math.min(99, safeNumber(book?.discountPercent, 0)));
  return percent > 0 ? `-${percent}%` : "";
}

const COVER_TONES = ["teal", "lav", "warm", "sage", "rose", "dark"];

function hashString(value) {
  const source = String(value || "");
  let hash = 0;
  for (let idx = 0; idx < source.length; idx += 1) {
    hash = (hash * 31 + source.charCodeAt(idx)) % 2147483647;
  }
  return Math.abs(hash);
}

function coverTone(book) {
  const seed = String(book?.id ?? book?.slug ?? book?.title ?? "");
  const index = hashString(seed) % COVER_TONES.length;
  return COVER_TONES[index];
}

function makeBookRoute(book) {
  const ident = book?.slug || book?.id;
  return ident == null ? "" : `/literature/books/${encodeURIComponent(String(ident))}`;
}

function mergeBookSaved(book, bookId, nextSaved) {
  if (!book || String(book.id) !== String(bookId)) return book;
  return { ...book, saved: nextSaved };
}

function defaultCatalog() {
  return {
    hero: { booksCount: 0, psychologistsCount: 0, topicsCount: 0 },
    topicFilters: [],
    featured: [],
    books: [],
    sections: [],
    latest: [],
    page: 0,
    size: CATALOG_PAGE_SIZE,
    total: 0,
  };
}

export default function Literature() {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const { role, isAuthenticated, me, logout } = useAuth();
  const { unreadNotificationsCount = 0 } = useNotifications();
  const [searchParams, setSearchParams] = useSearchParams();

  const initQuery = String(searchParams.get("q") || "");
  const initTopic = String(searchParams.get("topic") || "");
  const initPage = Math.max(0, safeNumber(searchParams.get("page"), 0));
  const initView = String(searchParams.get("view") || "grid").toLowerCase() === "list" ? "list" : "grid";
  const initSort = String(searchParams.get("sort") || "popular").toLowerCase();

  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [searchInput, setSearchInput] = React.useState(initQuery);
  const [query, setQuery] = React.useState(initQuery);
  const [topicFilter, setTopicFilter] = React.useState(initTopic);
  const [page, setPage] = React.useState(initPage);
  const [viewMode, setViewMode] = React.useState(initView);
  const [sortMode, setSortMode] = React.useState(["popular", "title", "year"].includes(initSort) ? initSort : "popular");

  const [catalogLoading, setCatalogLoading] = React.useState(true);
  const [catalogError, setCatalogError] = React.useState("");
  const [catalog, setCatalog] = React.useState(() => defaultCatalog());

  const [forMeLoading, setForMeLoading] = React.useState(true);
  const [forMeBooks, setForMeBooks] = React.useState([]);

  const [savedLoading, setSavedLoading] = React.useState(false);
  const [savedBooks, setSavedBooks] = React.useState([]);

  const [myBooksLoading, setMyBooksLoading] = React.useState(false);
  const [myBooks, setMyBooks] = React.useState([]);

  const [savingBookIds, setSavingBookIds] = React.useState(() => new Set());

  const canManageBooks = role === "ADMIN" || role === "MODERATOR" || role === "PSYCHOLOGIST";

  const origin = React.useMemo(() => getSiteOrigin(), []);
  const canonical = React.useMemo(() => {
    const queryParams = new URLSearchParams();
    if (query) queryParams.set("q", query);
    if (topicFilter) queryParams.set("topic", topicFilter);
    if (page > 0) queryParams.set("page", String(page));
    const suffix = queryParams.toString();
    return joinUrl(origin, `/literature${suffix ? `?${suffix}` : ""}`);
  }, [origin, query, topicFilter, page]);

  const breadcrumbs = React.useMemo(
    () => [
      { label: "Главная", to: "/" },
      { label: "Литература" },
    ],
    []
  );

  const reloadSavedBooks = React.useCallback(async () => {
    if (!isAuthenticated) {
      setSavedBooks([]);
      return;
    }
    setSavedLoading(true);
    try {
      const pageData = unwrapPage(await literatureApi.getMySaved({ page: 0, size: SAVED_LIMIT }));
      setSavedBooks(asArray(pageData.content).map(normalizeBook));
    } catch (error) {
      setSavedBooks([]);
    } finally {
      setSavedLoading(false);
    }
  }, [isAuthenticated]);

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      setPage(0);
      setQuery(String(searchInput || "").trim());
    }, 280);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  React.useEffect(() => {
    const next = new URLSearchParams();
    if (query) next.set("q", query);
    if (topicFilter) next.set("topic", topicFilter);
    if (page > 0) next.set("page", String(page));
    if (viewMode !== "grid") next.set("view", viewMode);
    if (sortMode !== "popular") next.set("sort", sortMode);
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [query, topicFilter, page, viewMode, sortMode, searchParams, setSearchParams]);

  React.useEffect(() => {
    let cancelled = false;
    const canUseReserveCatalog = !String(query || "").trim() && !String(topicFilter || "").trim();

    async function loadCatalog() {
      setCatalogLoading(true);
      setCatalogError("");

      try {
        const response = await literatureApi.getCatalog({ q: query, topic: topicFilter, page, size: CATALOG_PAGE_SIZE });
        if (cancelled) return;
        const normalized = normalizeCatalog(unwrapItems(response));
        setCatalog(normalized);
      } catch (error) {
        if (cancelled) return;

        if (canUseReserveCatalog) {
          try {
            const reserveResponse = await literatureApi.getLatest({ page, size: CATALOG_PAGE_SIZE });
            if (cancelled) return;
            const reservePage = unwrapPage(reserveResponse);
            const reserveRows = asArray(reservePage.content).map((entry) => {
              const row = asObject(entry);
              return {
                book: normalizeBook(row.book || row),
                topRecommendation: asObject(row.topRecommendation),
              };
            });
            const reserveBooks = reserveRows
              .map((row) => row.book)
              .filter((book) => book && (book.id != null || book.slug || book.title));

            if (reserveBooks.length) {
              const uniqueTopics = Array.from(
                new Set(
                  reserveBooks
                    .map((book) => String(book?.primaryTopic || "").trim())
                    .filter(Boolean)
                )
              );
              const uniqueExperts = new Set(
                reserveRows
                  .map((row) => String(row?.topRecommendation?.psychologistName || "").trim().toLowerCase())
                  .filter(Boolean)
              );
              const reserveTotal = Math.max(
                reserveBooks.length,
                safeNumber(firstFiniteNumber(reservePage.totalElements, reservePage.total), reserveBooks.length)
              );

              setCatalog({
                hero: {
                  booksCount: reserveTotal,
                  psychologistsCount: uniqueExperts.size,
                  topicsCount: uniqueTopics.length,
                },
                topicFilters: uniqueTopics,
                featured: reserveBooks.slice(0, 3),
                books: reserveBooks,
                sections: [],
                latest: reserveRows,
                page: Math.max(0, safeNumber(reservePage.number, page)),
                size: Math.max(1, safeNumber(reservePage.size, CATALOG_PAGE_SIZE)),
                total: reserveTotal,
              });
              setCatalogError("");
              return;
            }
          } catch {
            // no-op: fallback below will show mapped error
          }
        }

        setCatalog(defaultCatalog());
        setCatalogError(normalizeCatalogErrorMessage(error, "Не удалось загрузить литературу"));
      } finally {
        if (cancelled) return;
        setCatalogLoading(false);
      }
    }

    loadCatalog();

    return () => {
      cancelled = true;
    };
  }, [query, topicFilter, page]);

  React.useEffect(() => {
    let cancelled = false;
    setForMeLoading(true);
    literatureApi
      .getForMe({ page: 0, size: MINI_BOOKS_LIMIT })
      .then((response) => {
        if (cancelled) return;
        const pageData = unwrapPage(response);
        setForMeBooks(asArray(pageData.content).map(normalizeBook));
      })
      .catch(() => {
        if (cancelled) return;
        setForMeBooks([]);
      })
      .finally(() => {
        if (cancelled) return;
        setForMeLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    reloadSavedBooks();
  }, [reloadSavedBooks]);

  React.useEffect(() => {
    if (!canManageBooks) {
      setMyBooks([]);
      return;
    }
    let cancelled = false;
    setMyBooksLoading(true);
    literatureApi
      .getMyBooks({ page: 0, size: MY_BOOKS_LIMIT })
      .then((response) => {
        if (cancelled) return;
        const pageData = unwrapPage(response);
        setMyBooks(asArray(pageData.content).map(normalizeBook));
      })
      .catch(() => {
        if (cancelled) return;
        setMyBooks([]);
      })
      .finally(() => {
        if (cancelled) return;
        setMyBooksLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [canManageBooks]);

  const scrollToSection = React.useCallback((id, behavior = "auto") => {
    if (typeof document === "undefined") return false;
    const target = document.getElementById(String(id || "").trim());
    if (!target) return false;
    target.scrollIntoView({ behavior, block: "start" });
    return true;
  }, []);

  React.useEffect(() => {
    const hash = String(location.hash || "").replace(/^#/, "").trim();
    if (!hash) return;
    if (!["books-catalog", "saved-books", "my-books-section"].includes(hash)) return;

    let rafId = null;
    const didScroll = scrollToSection(hash, "auto");
    if (!didScroll) {
      rafId = window.requestAnimationFrame(() => {
        scrollToSection(hash, "auto");
      });
    }

    return () => {
      if (rafId != null) window.cancelAnimationFrame(rafId);
    };
  }, [location.hash, scrollToSection, canManageBooks, catalogLoading, savedLoading, myBooksLoading]);

  const catalogBooks = React.useMemo(() => {
    const base = asArray(catalog.books).slice();
    if (sortMode === "title") {
      return base.sort((a, b) => a.title.localeCompare(b.title, "ru"));
    }
    if (sortMode === "year") {
      return base.sort((a, b) => safeNumber(b.publishYear, 0) - safeNumber(a.publishYear, 0));
    }
    return base.sort((a, b) => safeNumber(b.recommendedByCount, 0) - safeNumber(a.recommendedByCount, 0));
  }, [catalog.books, sortMode]);

  const heroBook = React.useMemo(
    () => catalog.featured[0] || catalogBooks[0] || forMeBooks[0] || null,
    [catalog.featured, catalogBooks, forMeBooks]
  );

  const pageSize = Math.max(1, safeNumber(catalog.size, CATALOG_PAGE_SIZE));
  const totalPages = Math.max(1, Math.ceil(Math.max(0, safeNumber(catalog.total, 0)) / pageSize));
  const asideRecommendations = React.useMemo(
    () =>
      asArray(catalog.latest)
        .map((entry, index) => {
          const recommendation = asObject(entry?.topRecommendation);
          const book = entry?.book;
          if (!book) return null;
          return {
            id: book.id ?? `${book.title}-${index}`,
            rank: index + 1,
            book,
            author: String(recommendation.psychologistName || book.author || "").trim(),
            authorUserId:
              recommendation.psychologistUserId ??
              recommendation.userId ??
              book.authorUserId ??
              book.topRecommender?.userId ??
              null,
            authorProfileUrl: String(recommendation.profileUrl || book.authorProfileUrl || ""),
            badge: String(book.primaryTopic || book.categoryLabel || "").trim(),
          };
        })
        .filter(Boolean)
        .slice(0, 6),
    [catalog.latest]
  );

  const heroStats = React.useMemo(() => {
    const catalogBooks = asArray(catalog.books);
    const featuredBooks = asArray(catalog.featured);
    const latestRows = asArray(catalog.latest);

    const booksCount = Math.max(
      0,
      safeNumber(catalog?.hero?.booksCount, 0),
      safeNumber(catalog?.total, 0),
      catalogBooks.length,
      featuredBooks.length,
      latestRows.length,
      forMeBooks.length
    );

    const expertNames = new Set();
    latestRows.forEach((row) => {
      const recommendation = asObject(row?.topRecommendation);
      const book = asObject(row?.book);
      const name = String(
        recommendation.psychologistName ||
          book?.topRecommender?.name ||
          ""
      )
        .trim()
        .toLowerCase();
      if (name) expertNames.add(name);
    });

    [...catalogBooks, ...featuredBooks, ...forMeBooks].forEach((book) => {
      const name = String(book?.topRecommender?.name || "").trim().toLowerCase();
      if (name) expertNames.add(name);
    });

    const psychologistsCount = Math.max(
      0,
      safeNumber(catalog?.hero?.psychologistsCount, 0),
      expertNames.size
    );

    const topicNames = new Set(
      [
        ...asArray(catalog.topicFilters).map((value) => String(value || "").trim().toLowerCase()),
        ...catalogBooks.map((book) => String(book?.primaryTopic || "").trim().toLowerCase()),
        ...featuredBooks.map((book) => String(book?.primaryTopic || "").trim().toLowerCase()),
        ...forMeBooks.map((book) => String(book?.primaryTopic || "").trim().toLowerCase()),
      ].filter(Boolean)
    );

    const topicsCount = Math.max(
      0,
      safeNumber(catalog?.hero?.topicsCount, 0),
      topicNames.size
    );

    return { booksCount, psychologistsCount, topicsCount };
  }, [catalog, forMeBooks]);

  const pageNumbers = React.useMemo(() => {
    const radius = 2;
    const from = Math.max(0, page - radius);
    const to = Math.min(totalPages - 1, page + radius);
    const numbers = [];
    for (let idx = from; idx <= to; idx += 1) numbers.push(idx);
    return numbers;
  }, [page, totalPages]);

  const setSavedEverywhere = React.useCallback((bookId, nextSaved) => {
    setCatalog((prev) => ({
      ...prev,
      featured: asArray(prev.featured).map((item) => mergeBookSaved(item, bookId, nextSaved)),
      books: asArray(prev.books).map((item) => mergeBookSaved(item, bookId, nextSaved)),
      latest: asArray(prev.latest).map((entry) => ({
        ...entry,
        book: mergeBookSaved(entry.book, bookId, nextSaved),
      })),
      sections: asArray(prev.sections).map((section) => ({
        ...section,
        items: asArray(section.items).map((item) => mergeBookSaved(item, bookId, nextSaved)),
      })),
    }));
    setForMeBooks((prev) => asArray(prev).map((item) => mergeBookSaved(item, bookId, nextSaved)));
    setMyBooks((prev) => asArray(prev).map((item) => mergeBookSaved(item, bookId, nextSaved)));
  }, []);

  const handleToggleSave = React.useCallback(
    async (book, event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      const bookId = book?.id;
      if (!bookId) return;

      if (!isAuthenticated) {
        toast.info("Сохранение доступно после авторизации");
        navigate("/login");
        return;
      }

      if (savingBookIds.has(String(bookId))) return;
      setSavingBookIds((prev) => {
        const next = new Set(prev);
        next.add(String(bookId));
        return next;
      });

      try {
        const nextSaved = !Boolean(book.saved);
        if (nextSaved) await literatureApi.saveBook(bookId);
        else await literatureApi.unsaveBook(bookId);
        setSavedEverywhere(bookId, nextSaved);
        await reloadSavedBooks();
        toast.success(nextSaved ? "Книга сохранена" : "Книга удалена из сохранённых");
      } catch (error) {
        toast.error(error?.message || "Не удалось обновить сохранение");
      } finally {
        setSavingBookIds((prev) => {
          const next = new Set(prev);
          next.delete(String(bookId));
          return next;
        });
      }
    },
    [isAuthenticated, navigate, reloadSavedBooks, savingBookIds, setSavedEverywhere, toast]
  );

  const openBook = React.useCallback(
    (book) => {
      const route = makeBookRoute(book);
      if (!route) return;
      navigate(route);
    },
    [navigate]
  );

  const openPsychologistProfile = React.useCallback(
    (event, userId, profileUrl = "") => {
      const route = resolvePsychologistProfileRoute(profileUrl, userId);
      if (!route) return;
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      if (isExternalHttpUrl(route) && typeof window !== "undefined") {
        window.location.assign(route);
        return;
      }
      navigate(route);
    },
    [navigate]
  );

  const goToEditor = React.useCallback((book = null) => {
    if (!canManageBooks) {
      toast.info("Добавление книг доступно психологам, модераторам и администраторам");
      return;
    }
    const ident = book?.id ?? book?.slug;
    if (ident != null && String(ident).trim()) {
      navigate(`/dashboard/literature/new?bookId=${encodeURIComponent(String(ident))}`);
      return;
    }
    navigate("/dashboard/literature/new");
  }, [canManageBooks, navigate, toast]);

  const roleLabel = React.useMemo(() => displayRole(role), [role]);
  const maxRecommendedForMe = React.useMemo(() => {
    return Math.max(1, ...forMeBooks.map((book) => safeNumber(book.recommendedByCount, 0)));
  }, [forMeBooks]);

  const getReadingProgress = React.useCallback(
    (book) => {
      const count = Math.max(0, safeNumber(book?.recommendedByCount, 0));
      if (!maxRecommendedForMe) return 0;
      return Math.max(0, Math.min(100, Math.round((count / maxRecommendedForMe) * 100)));
    },
    [maxRecommendedForMe]
  );

  const getBookTypeMeta = React.useCallback((book) => {
    const source = `${book?.categoryLabel || ""} ${book?.difficultyLabel || ""}`.toLowerCase();
    if (source.includes("стать")) return { label: "Статья", className: "type-article" };
    if (source.includes("гид") || source.includes("guide")) return { label: "Гид", className: "type-guide" };
    return { label: "Книга", className: "type-book" };
  }, []);

  const getTagToneClass = React.useCallback((index) => {
    if (index === 0) return "tag-mint";
    if (index === 1) return "tag-lav";
    return "tag-grey";
  }, []);

  const progressByCatalog = React.useMemo(() => {
    if (!heroStats.booksCount) return 0;
    const percent = Math.round((savedBooks.length / Math.max(1, heroStats.booksCount)) * 100);
    return Math.max(0, Math.min(100, percent));
  }, [savedBooks.length, heroStats.booksCount]);

  const literatureQuickNavItems = React.useMemo(() => [
    { to: "/literature#books-catalog", label: "Каталог", icon: "▦" },
    canManageBooks
      ? { to: "/literature#my-books-section", label: "Мои книги", icon: "★" }
      : isAuthenticated
        ? { to: "/literature#saved-books", label: "Сохранённые", icon: "★" }
        : null,
    canManageBooks ? { label: "Добавить", icon: "+", accent: "create", onClick: () => goToEditor() } : null,
  ].filter(Boolean), [canManageBooks, goToEditor, isAuthenticated]);

  return (
    <div className="podcasts-hub literature-hub-shell">
      <Helmet prioritizeSeoTags>
        <title>Литература | Бюро Души</title>
        <meta
          name="description"
          content="Библиотека психологической литературы: подборки, комментарии специалистов, сохранение и рекомендации."
        />
        {canonical ? <link rel="canonical" href={canonical} /> : null}
      </Helmet>

      <BreadcrumbsSchema items={breadcrumbs} />

      <div className="podcasts-shell">
        <AppSidebarV2
          activeKey="literature"
          mobileOpen={sidebarOpen}
          onCloseMobile={() => setSidebarOpen(false)}
          userName={me?.name || me?.login || "Гость"}
          roleLabel={roleLabel}
          canOpenLiteratureEditor={canManageBooks}
          notificationsBadge={unreadNotificationsCount}
          onLogout={logout}
        />

        <button
          className={`podcasts-overlay ${sidebarOpen ? "is-open" : ""}`}
          type="button"
          aria-label="Закрыть меню"
          onClick={() => setSidebarOpen(false)}
        />

        <div className="podcasts-main">
          <header className="podcasts-topbar literature-topbar">
            <div className="podcasts-topbar__left">
              <button
                className="podcasts-topbar__menu-btn"
                type="button"
                onClick={() => setSidebarOpen(true)}
                aria-label="Открыть меню"
              >
                ☰
              </button>
              <div className="literature-topbar__title">Литература</div>
              <div className="podcasts-topbar__search topbar-search">
                <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <circle cx="9" cy="9" r="6" />
                  <line x1="13.5" y1="13.5" x2="18" y2="18" strokeLinecap="round" />
                </svg>
                <input
                  type="search"
                  placeholder="Найти книгу, автора, тему…"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                />
              </div>
            </div>

            <div className="podcasts-topbar__right">
              <div className="view-toggle">
                <button
                  type="button"
                  className={`view-btn ${viewMode === "grid" ? "active" : ""}`}
                  onClick={() => setViewMode("grid")}
                  title="Сетка"
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                    <rect x="1" y="1" width="6" height="6" rx="1.5" />
                    <rect x="9" y="1" width="6" height="6" rx="1.5" />
                    <rect x="1" y="9" width="6" height="6" rx="1.5" />
                    <rect x="9" y="9" width="6" height="6" rx="1.5" />
                  </svg>
                </button>
                <button
                  type="button"
                  className={`view-btn ${viewMode === "list" ? "active" : ""}`}
                  onClick={() => setViewMode("list")}
                  title="Список"
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <line x1="1" y1="4" x2="15" y2="4" strokeLinecap="round" />
                    <line x1="1" y1="8" x2="15" y2="8" strokeLinecap="round" />
                    <line x1="1" y1="12" x2="15" y2="12" strokeLinecap="round" />
                  </svg>
                </button>
              </div>

              <select
                className="sort-select"
                value={sortMode}
                onChange={(event) => setSortMode(event.target.value)}
              >
                <option value="popular">По популярности</option>
                <option value="year">Сначала новые</option>
                <option value="title">По алфавиту</option>
              </select>

              {canManageBooks ? (
                <button type="button" className="btn-upload" onClick={goToEditor}>
                  Добавить книгу
                </button>
              ) : null}

              <button className="podcasts-topbar__avatar" type="button" onClick={() => navigate("/dashboard/settings")}>
                {String(me?.name || me?.login || "BS").trim().slice(0, 2).toUpperCase()}
              </button>
            </div>
          </header>

          <main className="page-content catalog-screen">
            {heroBook ? (
              <section
                className="hero-block"
                role="button"
                tabIndex={0}
                onClick={() => openBook(heroBook)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    openBook(heroBook);
                  }
                }}
              >
                <div className={`hero-cover cover-${coverTone(heroBook)}`}>
                  {heroBook.coverUrl ? (
                    <img src={heroBook.coverUrl} alt={heroBook.title} loading="lazy" />
                  ) : (
                    <>
                      <div className="hero-cover-bg" />
                      <div className="hero-cover-accent" />
                      <div className="hero-cover-shape" />
                      <div className="hero-cover-shape2" />
                      <div className="hero-cover-title">{shortText(heroBook.title, 34)}</div>
                      <div className="hero-cover-author">
                        {heroBook.authorUserId || heroBook.authorProfileUrl ? (
                          <button
                            type="button"
                            className="b-psych-link-btn"
                            onClick={(event) =>
                              openPsychologistProfile(event, heroBook.authorUserId, heroBook.authorProfileUrl)
                            }
                          >
                            {shortText(heroBook.author, 26)}
                          </button>
                        ) : (
                          shortText(heroBook.author, 26)
                        )}
                      </div>
                    </>
                  )}
                </div>

                <div className="hero-info">
                  <div className="hero-label">
                    <div className="hero-label-dot" />
                    Книга месяца
                  </div>
                  <h2 className="hero-book-title">{heroBook.title}</h2>
                  <div className="hero-book-author">
                    {heroBook.authorUserId || heroBook.authorProfileUrl ? (
                      <button
                        type="button"
                        className="b-psych-link-btn"
                        onClick={(event) =>
                          openPsychologistProfile(event, heroBook.authorUserId, heroBook.authorProfileUrl)
                        }
                      >
                        {heroBook.author}
                      </button>
                    ) : (
                      heroBook.author
                    )}
                    {heroBook.publishYear ? ` · ${heroBook.publishYear}` : ""}
                  </div>
                  <div className="hero-book-desc">
                    {shortText(heroBook.shortDescription || "Подборка литературы от специалистов платформы.", 220)}
                  </div>
                  <div className="hero-tags">
                    {[
                      heroBook.primaryTopic,
                      heroBook.categoryLabel,
                      heroBook.difficultyLabel,
                      heroBook.publishYear ? `${heroBook.publishYear}` : "",
                    ]
                      .filter(Boolean)
                      .slice(0, 4)
                      .map((label, index) => (
                        <span key={`${label}-${index}`} className={`tag ${getTagToneClass(index)}`}>
                          {label}
                        </span>
                      ))}
                  </div>
                  <div className="hero-cta-row">
                    <button
                      type="button"
                      className="btn-read"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        openBook(heroBook);
                      }}
                    >
                      Читать
                    </button>
                    <button
                      type="button"
                      className={`btn-save-hero ${heroBook.saved ? "saved" : ""}`}
                      onClick={(event) => handleToggleSave(heroBook, event)}
                      disabled={savingBookIds.has(String(heroBook.id))}
                      title={heroBook.saved ? "Удалить из сохранённых" : "Сохранить"}
                    >
                      <svg width="15" height="15" viewBox="0 0 20 20" fill="none">
                        {heroBook.saved ? (
                          <path d="M5 2h10a1 1 0 0 1 1 1v15l-6-3.5L4 18V3a1 1 0 0 1 1-1z" fill="currentColor" />
                        ) : (
                          <path
                            d="M5 2h10a1 1 0 0 1 1 1v15l-6-3.5L4 18V3a1 1 0 0 1 1-1z"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        )}
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="hero-stats">
                  <div className="hero-stat">
                    <div className="hero-stat-val mint">{formatNumber(heroStats.booksCount)}</div>
                    <div className="hero-stat-label">Книг</div>
                  </div>
                  <div className="hero-stat">
                    <div className="hero-stat-val">{formatNumber(heroStats.psychologistsCount)}</div>
                    <div className="hero-stat-label">Специалистов</div>
                  </div>
                  <div className="hero-stat">
                    <div className="hero-stat-val">{formatNumber(heroStats.topicsCount)}</div>
                    <div className="hero-stat-label">Тем</div>
                  </div>
                </div>
              </section>
            ) : (
              <div className="empty-state">
                <div className="empty-text">Не удалось загрузить главную карточку литературы</div>
              </div>
            )}

            <section className="filter-bar">
              <span className="filter-label">Раздел</span>
              <div className="filter-chips">
                <button
                  type="button"
                  className={`chip ${!topicFilter ? "active chip-mint" : ""}`}
                  onClick={() => {
                    setTopicFilter("");
                    setPage(0);
                  }}
                >
                  Все
                </button>
                {catalog.topicFilters.map((topicName) => {
                  const active = topicName.toLowerCase() === topicFilter.toLowerCase();
                  return (
                    <button
                      type="button"
                      key={topicName}
                      className={`chip ${active ? "active chip-mint" : ""}`}
                      onClick={() => {
                        setTopicFilter(topicName);
                        setPage(0);
                      }}
                    >
                      {topicName}
                    </button>
                  );
                })}
              </div>
              <div className="filter-sort">
                <span>{formatNumber(catalog.total)} материалов</span>
              </div>
            </section>

            <section className="content-block" id="books-catalog">
              <div className="section-header-row">
                <div className="section-heading">Читаю сейчас</div>
                <span className="section-link">{forMeBooks.length ? `${forMeBooks.length} книг` : "Подборка"}</span>
              </div>

              {forMeLoading ? (
                <div className="empty-state">
                  <div className="empty-sub">Загрузка подборки…</div>
                </div>
              ) : null}

              {!forMeLoading && !forMeBooks.length ? (
                <div className="empty-state">
                  <div className="empty-text">Персональная подборка пока пуста</div>
                  <div className="empty-sub">Добавьте книги в сохранённое — они появятся здесь</div>
                </div>
              ) : null}

              {!forMeLoading && forMeBooks.length ? (
                <div className="scroll-track-wrapper">
                  <div className="scroll-track">
                    {forMeBooks.map((book, index) => {
                      const progress = getReadingProgress(book);
                      return (
                        <article
                          key={`for-me-${book.id || index}`}
                          className="book-card-sm"
                          role="button"
                          tabIndex={0}
                          onClick={() => openBook(book)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              openBook(book);
                            }
                          }}
                        >
                          <div className={`book-cover-sm cover-${coverTone(book)}`}>
                            {book.coverUrl ? (
                              <img src={book.coverUrl} alt={book.title} loading="lazy" />
                            ) : (
                              <>
                                <div className="cover-pattern-circ" />
                                <div className="cover-inner">
                                  <div className="cover-book-title">{shortText(book.title, 34)}</div>
                                  <div className="cover-book-author">
                                    {book.authorUserId || book.authorProfileUrl ? (
                                      <button
                                        type="button"
                                        className="b-psych-link-btn"
                                        onClick={(event) =>
                                          openPsychologistProfile(event, book.authorUserId, book.authorProfileUrl)
                                        }
                                      >
                                        {shortText(book.author, 28)}
                                      </button>
                                    ) : (
                                      shortText(book.author, 28)
                                    )}
                                  </div>
                                </div>
                              </>
                            )}
                            <button
                              type="button"
                              className={`cover-save-btn ${book.saved ? "saved" : ""}`}
                              onClick={(event) => handleToggleSave(book, event)}
                              disabled={savingBookIds.has(String(book.id))}
                            >
                              <svg width="11" height="11" viewBox="0 0 20 20" fill="none">
                                {book.saved ? (
                                  <path d="M5 2h10a1 1 0 0 1 1 1v15l-6-3.5L4 18V3a1 1 0 0 1 1-1z" fill="currentColor" />
                                ) : (
                                  <path
                                    d="M5 2h10a1 1 0 0 1 1 1v15l-6-3.5L4 18V3a1 1 0 0 1 1-1z"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  />
                                )}
                              </svg>
                            </button>
                            {discountLabel(book) ? <span className="book-cover-discount">{discountLabel(book)}</span> : null}
                          </div>
                          <div className="progress-bar-wrap">
                            <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
                          </div>
                          <div className="book-card-sm-title">{book.title}</div>
                          <div className="book-card-sm-author">
                            {progress > 0 ? `${progress}% · рекомендаций ${formatNumber(book.recommendedByCount)}` : "Ещё не начата"}
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </section>

            <div className="content-with-aside">
              <section>
                <div className="section-header-row section-header-row--catalog">
                  <div className="section-heading">Каталог</div>
                  <span className="section-link">{catalogError ? "—" : `${formatNumber(catalog.total)} результатов`}</span>
                </div>

                {catalogLoading ? (
                  <div className="empty-state">
                    <div className="empty-sub">Загрузка литературы…</div>
                  </div>
                ) : null}

                {!catalogLoading && catalogError ? (
                  <div className="empty-state">
                    <div className="empty-text">{catalogError}</div>
                  </div>
                ) : null}

                {!catalogLoading && !catalogError && !catalogBooks.length ? (
                  <div className="empty-state">
                    <div className="empty-text">По выбранным фильтрам книги не найдены</div>
                    <div className="empty-sub">Сбросьте фильтры или измените поисковый запрос</div>
                  </div>
                ) : null}

                {!catalogLoading && !catalogError && catalogBooks.length ? (
                  viewMode === "list" ? (
                    <div className="books-list">
                      {catalogBooks.map((book, idx) => (
                        <article key={`list-${book.id || idx}`} className="book-list-item">
                          <button type="button" className="book-list-main" onClick={() => openBook(book)}>
                            <div className={`book-list-cover cover-${coverTone(book)}`}>
                              {book.coverUrl ? <img src={book.coverUrl} alt={book.title} loading="lazy" /> : null}
                              {discountLabel(book) ? <span className="book-cover-discount">{discountLabel(book)}</span> : null}
                            </div>
                            <div className="book-list-info">
                              <div className="book-list-title">{book.title}</div>
                              <div className="book-list-author">
                                {book.authorUserId || book.authorProfileUrl ? (
                                  <button
                                    type="button"
                                    className="b-psych-link-btn"
                                    onClick={(event) =>
                                      openPsychologistProfile(event, book.authorUserId, book.authorProfileUrl)
                                    }
                                  >
                                    {book.author}
                                  </button>
                                ) : (
                                  book.author
                                )}
                                {book.publishYear ? ` · ${book.publishYear}` : ""}
                              </div>
                              <div className="book-list-tags">
                                {book.primaryTopic ? <span className="book-tag">{book.primaryTopic}</span> : null}
                                {book.categoryLabel ? <span className="book-tag">{book.categoryLabel}</span> : null}
                              </div>
                            </div>
                          </button>
                          <div className="book-list-meta">
                            <div className="book-list-stat">
                              <div className="book-list-stat-val">{formatNumber(book.recommendedByCount)}</div>
                              <div className="book-list-stat-label">Рекомендаций</div>
                            </div>
                            <div className="book-list-stat">
                              <div className="book-list-stat-val">{book.publishYear || "—"}</div>
                              <div className="book-list-stat-label">Год</div>
                            </div>
                          </div>
                          <div className="book-list-actions">
                            <button
                              type="button"
                              className={`list-action-btn ${book.saved ? "saved" : ""}`}
                              onClick={(event) => handleToggleSave(book, event)}
                              disabled={savingBookIds.has(String(book.id))}
                              title={book.saved ? "Удалить из сохранённых" : "Сохранить"}
                            >
                              <svg width="13" height="13" viewBox="0 0 20 20" fill="none">
                                {book.saved ? (
                                  <path d="M5 2h10a1 1 0 0 1 1 1v15l-6-3.5L4 18V3a1 1 0 0 1 1-1z" fill="currentColor" />
                                ) : (
                                  <path d="M5 2h10a1 1 0 0 1 1 1v15l-6-3.5L4 18V3a1 1 0 0 1 1-1z" stroke="currentColor" strokeWidth="2" />
                                )}
                              </svg>
                            </button>
                            <button type="button" className="list-action-btn" title="Читать" onClick={() => openBook(book)}>
                              <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M5 3h10l2 3H3L5 3z" />
                                <rect x="3" y="6" width="14" height="11" rx="1" />
                              </svg>
                            </button>
                          </div>
                        </article>
                      ))}
                    </div>
                  ) : (
                    <div className="books-grid">
                      {catalogBooks.map((book, idx) => {
                        const type = getBookTypeMeta(book);
                        return (
                          <article
                            key={`grid-${book.id || idx}`}
                            className="book-card-md"
                            role="button"
                            tabIndex={0}
                            onClick={() => openBook(book)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                openBook(book);
                              }
                            }}
                          >
                            <div className={`book-cover-md cover-${coverTone(book)}`}>
                              {book.coverUrl ? (
                                <img src={book.coverUrl} alt={book.title} loading="lazy" />
                              ) : (
                                <>
                                  <div className="cover-pattern-circ cover-pattern-circ--md" />
                                  <div className="cover-inner">
                                    <div className="cover-book-title">{shortText(book.title, 42)}</div>
                                    <div className="cover-book-author">
                                      {book.authorUserId || book.authorProfileUrl ? (
                                        <button
                                          type="button"
                                          className="b-psych-link-btn"
                                          onClick={(event) =>
                                            openPsychologistProfile(event, book.authorUserId, book.authorProfileUrl)
                                          }
                                        >
                                          {shortText(book.author, 28)}
                                        </button>
                                      ) : (
                                        shortText(book.author, 28)
                                      )}
                                    </div>
                                  </div>
                                </>
                              )}
                              <span className={`book-cover-type ${type.className}`}>{type.label}</span>
                              {discountLabel(book) ? <span className="book-cover-discount">{discountLabel(book)}</span> : null}
                              <div className="book-cover-md-actions">
                                <button
                                  type="button"
                                  className={`cover-action-btn ${book.saved ? "saved" : ""}`}
                                  onClick={(event) => handleToggleSave(book, event)}
                                  disabled={savingBookIds.has(String(book.id))}
                                >
                                  <svg width="13" height="13" viewBox="0 0 20 20" fill="none">
                                    {book.saved ? (
                                      <path d="M5 2h10a1 1 0 0 1 1 1v15l-6-3.5L4 18V3a1 1 0 0 1 1-1z" fill="currentColor" />
                                    ) : (
                                      <path
                                        d="M5 2h10a1 1 0 0 1 1 1v15l-6-3.5L4 18V3a1 1 0 0 1 1-1z"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                      />
                                    )}
                                  </svg>
                                </button>
                                <button type="button" className="cover-action-btn" onClick={() => openBook(book)} title="Подробнее">
                                  <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="10" cy="10" r="8" />
                                    <line x1="10" y1="7" x2="10" y2="10" />
                                    <circle cx="10" cy="13" r="1" fill="currentColor" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                            <div className="book-card-md-body">
                              <div className="book-card-md-title">{book.title}</div>
                              <div className="book-card-md-author">
                                {book.authorUserId || book.authorProfileUrl ? (
                                  <button
                                    type="button"
                                    className="b-psych-link-btn"
                                    onClick={(event) =>
                                      openPsychologistProfile(event, book.authorUserId, book.authorProfileUrl)
                                    }
                                  >
                                    {book.author}
                                  </button>
                                ) : (
                                  book.author
                                )}
                              </div>
                              <div className="book-card-md-tags">
                                {book.primaryTopic ? <span className="book-tag">{book.primaryTopic}</span> : null}
                                {book.categoryLabel ? <span className="book-tag">{book.categoryLabel}</span> : null}
                              </div>
                              <div className="book-card-md-footer">
                                <div className="book-pages">{book.publishYear || "—"}</div>
                                <div className="book-rating">
                                  <span className="star-icon">★</span> {formatNumber(book.recommendedByCount)}
                                </div>
                              </div>
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  )
                ) : null}

                {!catalogLoading && !catalogError && totalPages > 1 ? (
                  <div className="literature-pagination">
                    <button type="button" onClick={() => setPage((prev) => Math.max(0, prev - 1))} disabled={page <= 0}>
                      ←
                    </button>
                    {pageNumbers.map((pageNumber) => (
                      <button
                        key={`page-${pageNumber}`}
                        type="button"
                        className={pageNumber === page ? "active" : ""}
                        onClick={() => setPage(pageNumber)}
                      >
                        {pageNumber + 1}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => setPage((prev) => Math.min(totalPages - 1, prev + 1))}
                      disabled={page >= totalPages - 1}
                    >
                      →
                    </button>
                  </div>
                ) : null}
              </section>

              <aside className="aside-panel">
                <section className="aside-card" id="saved-books">
                  <div className="aside-card-title">Ваша полка</div>
                  <div className="stats-row">
                    <div className="stat-tile">
                      <div className="stat-tile-val mint">{formatNumber(savedBooks.length)}</div>
                      <div className="stat-tile-label">Сохранено</div>
                    </div>
                    <div className="stat-tile">
                      <div className="stat-tile-val lavender">{formatNumber(forMeBooks.length)}</div>
                      <div className="stat-tile-label">Читаю</div>
                    </div>
                    <div className="stat-tile">
                      <div className="stat-tile-val">{formatNumber(heroStats.booksCount)}</div>
                      <div className="stat-tile-label">В каталоге</div>
                    </div>
                    <div className="stat-tile">
                      <div className="stat-tile-val mint">↑{progressByCatalog}%</div>
                      <div className="stat-tile-label">Прогресс</div>
                    </div>
                  </div>
                </section>

                <section className="aside-card">
                  <div className="aside-card-title">
                    Сохранённые
                    {isAuthenticated ? <Link className="aside-card-link" to="/dashboard/settings">Все →</Link> : null}
                  </div>
                  {savedLoading ? <div className="empty-sub">Загрузка…</div> : null}
                  {!savedLoading && !savedBooks.length ? (
                    <div className="empty-sub">
                      {isAuthenticated ? "У вас пока нет сохранённых книг" : "Войдите в аккаунт, чтобы сохранять книги"}
                    </div>
                  ) : null}
                  {!savedLoading && savedBooks.length ? (
                    <div className="saved-list">
                      {savedBooks.slice(0, SAVED_LIMIT).map((book, idx) => (
                        <div
                          key={`saved-${book.id || idx}`}
                          className="saved-item"
                          role="button"
                          tabIndex={0}
                          onClick={() => openBook(book)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              openBook(book);
                            }
                          }}
                        >
                          <div className={`saved-cover cover-${coverTone(book)}`}>
                            {book.coverUrl ? <img src={book.coverUrl} alt={book.title} loading="lazy" /> : null}
                          </div>
                          <div className="saved-info">
                            <div className="saved-title">{book.title}</div>
                            <div className="saved-author">
                              {book.authorUserId || book.authorProfileUrl ? (
                                <button
                                  type="button"
                                  className="b-psych-link-btn"
                                  onClick={(event) =>
                                    openPsychologistProfile(event, book.authorUserId, book.authorProfileUrl)
                                  }
                                >
                                  {book.author}
                                </button>
                              ) : (
                                book.author
                              )}
                            </div>
                            <div className="saved-progress">
                              <div className="saved-progress-fill" style={{ width: `${getReadingProgress(book)}%` }} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </section>

                <section className="aside-card">
                  <div className="aside-card-title">Рекомендуем</div>
                  {!asideRecommendations.length ? (
                    <div className="empty-sub">Пока нет рекомендаций</div>
                  ) : (
                    <div className="rec-list">
                      {asideRecommendations.map((item) => (
                        <div
                          key={`aside-rec-${item.id}`}
                          className="rec-item"
                          role="button"
                          tabIndex={0}
                          onClick={() => openBook(item.book)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              openBook(item.book);
                            }
                          }}
                        >
                          <div className="rec-num">{String(item.rank).padStart(2, "0")}</div>
                          <div className="rec-info">
                            <div className="rec-title">{item.book.title}</div>
                            <div className="rec-author">
                              {item.authorUserId || item.authorProfileUrl ? (
                                <button
                                  type="button"
                                  className="b-psych-link-btn"
                                  onClick={(event) =>
                                    openPsychologistProfile(event, item.authorUserId, item.authorProfileUrl)
                                  }
                                >
                                  {item.author || "Специалист платформы"}
                                </button>
                              ) : (
                                item.author || "Специалист платформы"
                              )}
                            </div>
                            {item.badge ? <span className="rec-tag tag-mint">{item.badge}</span> : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              </aside>
            </div>

            {canManageBooks ? (
              <section className="my-books-section" id="my-books-section">
                <div className="my-books-header">
                  <div className="my-books-title-group">
                    <div className="my-books-accent-bar" />
                    <div>
                      <div className="my-books-title">Мои книги</div>
                      <div className="my-books-count">{formatNumber(myBooks.length)} материалов</div>
                    </div>
                  </div>
                  <div className="my-books-actions">
                    <button type="button" className="btn-new-book" onClick={goToEditor}>
                      Добавить книгу
                    </button>
                  </div>
                </div>

                {myBooksLoading ? (
                  <div className="my-books-empty">
                    <div className="my-books-empty-sub">Загрузка ваших книг…</div>
                  </div>
                ) : null}

                {!myBooksLoading && !myBooks.length ? (
                  <div className="my-books-empty">
                    <div className="my-books-empty-icon">📚</div>
                    <div className="my-books-empty-title">Пока нет опубликованных книг</div>
                    <div className="my-books-empty-sub">Создайте первую публикацию, и она появится в этом блоке</div>
                  </div>
                ) : null}

                {!myBooksLoading && myBooks.length ? (
                  <div className="my-books-grid">
                    {myBooks.map((book, idx) => (
                      <article
                        key={`mine-${book.id || idx}`}
                        className="my-book-card"
                        role="button"
                        tabIndex={0}
                        onClick={() => openBook(book)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            openBook(book);
                          }
                        }}
                      >
                        <div className={`my-book-cover cover-${coverTone(book)}`}>
                          {book.coverUrl ? <img src={book.coverUrl} alt={book.title} loading="lazy" /> : null}
                          <span className="my-badge">Моя</span>
                          {discountLabel(book) ? <span className="book-cover-discount">{discountLabel(book)}</span> : null}
                          <span className="pub-badge published">Опубликовано</span>
                        </div>
                        <div className="my-book-body">
                          <div className="my-book-title">{book.title}</div>
                          <div className="my-book-meta">
                            {book.authorUserId || book.authorProfileUrl ? (
                              <button
                                type="button"
                                className="b-psych-link-btn"
                                onClick={(event) =>
                                  openPsychologistProfile(event, book.authorUserId, book.authorProfileUrl)
                                }
                              >
                                {book.author}
                              </button>
                            ) : (
                              book.author
                            )}
                          </div>
                          <div className="my-book-stats">
                            <div className="my-book-stat">
                              <strong>{formatNumber(book.recommendedByCount)}</strong>
                              <span>рекомендаций</span>
                            </div>
                            <div className="my-book-stat">
                              <strong>{book.publishYear || "—"}</strong>
                              <span>год</span>
                            </div>
                          </div>
                          <div className="my-book-footer">
                            <button
                              type="button"
                              className="my-btn-sm view"
                              onClick={(event) => {
                                event.stopPropagation();
                                openBook(book);
                              }}
                            >
                              Открыть
                            </button>
                            <button
                              type="button"
                              className="my-btn-sm edit"
                              onClick={(event) => {
                                event.stopPropagation();
                                goToEditor(book);
                              }}
                            >
                              Редактировать
                            </button>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                ) : null}
              </section>
            ) : null}
          </main>
          <MobileQuickNav items={literatureQuickNavItems} />
        </div>
      </div>
    </div>
  );
}
